import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/apiClient';
import { Card, CardContent } from '../Components/ui/card';
import { Button } from '../Components/ui/button';
import { Badge } from '../Components/ui/badge';
import { Select, SelectItem, SelectTrigger, SelectValue, SelectContent } from '../Components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../Components/ui/dialog';
import { Input } from '../Components/ui/input';
import { Label } from '../Components/ui/label';
import { Loader2, RefreshCw, History, UserCog, Truck, Search, Filter } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { format } from 'date-fns';

export default function ListBatches() {
    const queryClient = useQueryClient();
    const { addToast } = useToast();
    const [reassignmentData, setReassignmentData] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");

    // Fetch batch staff
    const { data: staffList } = useQuery({
        queryKey: ['batchStaff'],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/batch-staff');
            return data;
        },
    });

    // Fetch All Batches
    const { data: allBatches, isLoading: loadingBatches } = useQuery({
        queryKey: ['adminAllBatches'],
        queryFn: async () => {
            const { data } = await apiClient.get('/admin/batches');
            return data;
        },
    });

    const reassignBatchMutation = useMutation({
        mutationFn: async ({ batchId, staffId }) => {
            const { data } = await apiClient.put(`/admin/batches/${batchId}/reassign`, { assigned_to: staffId });
            return data;
        },
        onSuccess: () => {
            addToast('Batch reassigned successfully!', 'success');
            queryClient.invalidateQueries(['adminAllBatches']);
            setReassignmentData(null);
        },
        onError: (err) => {
            addToast(err.response?.data?.msg || 'Failed to reassign batch', 'error');
        }
    });

    const filteredAndSortedBatches = useMemo(() => {
        if (!allBatches) return [];

        let result = [...allBatches];

        // Filter by Search Term (Batch ID, Staff Name, Item Name)
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(batch => {
                const matchId = batch.batchId?.toLowerCase().includes(lowerSearch);
                const matchStaff = batch.assigned_to?.name?.toLowerCase().includes(lowerSearch);
                const matchItem = batch.items?.some(i => i.item_name?.toLowerCase().includes(lowerSearch));
                return matchId || matchStaff || matchItem;
            });
        }

        // Filter by Status
        if (filterStatus !== "all") {
            if (filterStatus === "delivered") {
                result = result.filter(batch => batch.status === 'delivered' || batch.status === 'Completed');
            } else if (filterStatus === "active") {
                result = result.filter(batch => batch.status !== 'delivered' && batch.status !== 'Completed');
            }
        }

        // Sort: Active/New batches first, delivered last. Then by created date descending
        result.sort((a, b) => {
            const isADelivered = a.status === 'delivered' || a.status === 'Completed';
            const isBDelivered = b.status === 'delivered' || b.status === 'Completed';
            if (isADelivered && !isBDelivered) return 1;
            if (!isADelivered && isBDelivered) return -1;
            return new Date(b.createdAt) - new Date(a.createdAt);
        });

        return result;
    }, [allBatches, searchTerm, filterStatus]);

    return (
        <div className="p-6 lg:p-10 bg-zinc-50 dark:bg-black min-h-screen text-zinc-900 dark:text-zinc-100 pb-24 lg:pb-8">
            <div className="max-w-7xl mx-auto space-y-8">

                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center py-6 border-b border-zinc-200 dark:border-zinc-800">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
                            List of All Batches
                        </h1>
                        <p className="text-zinc-500 dark:text-zinc-400 mt-1 text-lg">
                            Monitor, search, and manage delivery personnel assignments.
                        </p>
                    </div>
                </div>

                <div className="bg-white dark:bg-black rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden min-h-[600px]">
                    {/* Toolbar */}
                    <div className="p-6 border-b border-zinc-100 dark:border-zinc-800 flex flex-col md:flex-row justify-between items-center bg-zinc-50/50 dark:bg-zinc-900/50 gap-4">
                        <div className="flex-1 flex flex-col sm:flex-row items-center gap-4 w-full">
                            <div className="relative w-full sm:max-w-xs">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 w-4 h-4" />
                                <Input
                                    type="text"
                                    placeholder="Search batches..."
                                    className="pl-9 bg-white dark:bg-zinc-950 w-full"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="w-full sm:max-w-[150px]">
                                <Select value={filterStatus} onValueChange={setFilterStatus}>
                                    <SelectTrigger className="w-full bg-white dark:bg-zinc-950">
                                        <Filter className="w-4 h-4 mr-2 text-zinc-400" />
                                        <SelectValue placeholder="Filter" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All Statuses</SelectItem>
                                        <SelectItem value="active">Active (Assigned/In Progress)</SelectItem>
                                        <SelectItem value="delivered">Delivered</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => queryClient.invalidateQueries(['adminAllBatches'])} className="bg-white dark:bg-zinc-900 w-full md:w-auto mt-2 md:mt-0">
                            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                        </Button>
                    </div>

                    <div className="p-6 grid gap-6">
                        {loadingBatches ? (
                            <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
                                <Loader2 className="w-10 h-10 animate-spin mb-4" />
                                Loading fleet data...
                            </div>
                        ) : filteredAndSortedBatches.length > 0 ? (
                            filteredAndSortedBatches.map(batch => (
                                <div key={batch._id} className="group flex flex-col xl:flex-row bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl hover:shadow-lg transition-all duration-300">

                                    {/* Status Strip */}
                                    <div className={`w-full xl:w-2 h-2 xl:h-auto rounded-t-xl xl:rounded-l-xl xl:rounded-tr-none ${(batch.status === 'delivered' || batch.status === 'Completed') ? 'bg-emerald-500' :
                                        batch.status === 'in_progress' ? 'bg-blue-500' :
                                            'bg-amber-500'}`}
                                    />

                                    <div className="flex-1 p-6 flex flex-col md:flex-row gap-6">
                                        {/* Left: Info */}
                                        <div className="flex-1 space-y-3">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-xl font-mono font-bold tracking-tight">{batch.batchId}</h3>
                                                <Badge variant={(batch.status === 'delivered' || batch.status === 'Completed') ? 'default' : 'secondary'} className={(batch.status === 'delivered' || batch.status === 'Completed') ? 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20' : ''}>
                                                    {batch.status.replace('_', ' ')}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-zinc-500 flex items-center gap-2">
                                                <History className="w-4 h-4" /> Created on {format(new Date(batch.createdAt), 'PPP')}
                                            </p>

                                            {/* Mini Item Preview (limit 3) */}
                                            <div className="flex flex-wrap gap-2 pt-2">
                                                {batch.items.slice(0, 3).map((item, i) => (
                                                    <div key={i} className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 rounded text-xs font-medium border border-zinc-200 dark:border-zinc-700">
                                                        {item.item_name} (x{item.quantity})
                                                    </div>
                                                ))}
                                                {batch.items.length > 3 && (
                                                    <span className="text-xs text-zinc-500 self-center">+{batch.items.length - 3} more</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right: Actions */}
                                        <div className="w-full md:w-80 bg-zinc-50 dark:bg-zinc-950/50 rounded-lg p-5 border border-zinc-100 dark:border-zinc-800/50 flex flex-col gap-4 justify-center">
                                            {(batch.status !== 'delivered' && batch.status !== 'Completed') && (
                                                <div>
                                                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2 block">
                                                        Reassign Logistics Partner
                                                    </Label>
                                                    <Select
                                                        value={batch.assigned_to?._id || ""}
                                                        onValueChange={(val) => {
                                                            const staffMember = staffList?.find(s => s._id === val);
                                                            if (val && staffMember && val !== batch.assigned_to?._id) {
                                                                setReassignmentData({
                                                                    batchId: batch._id,
                                                                    batchDisplayId: batch.batchId,
                                                                    staffId: val,
                                                                    staffName: staffMember.name
                                                                });
                                                            }
                                                        }}
                                                    >
                                                        <SelectTrigger className="w-full bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800">
                                                            <SelectValue placeholder="Select Staff">
                                                                {batch.assigned_to ? batch.assigned_to.name : "Unassigned"}
                                                            </SelectValue>
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {staffList?.map(staff => (
                                                                <SelectItem key={staff._id} value={staff._id}>
                                                                    {staff.name}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            )}

                                            <div className="text-xs text-zinc-400 flex items-center gap-1">
                                                {batch.assigned_to ? (
                                                    <span className="text-emerald-600 dark:text-emerald-500 font-medium flex items-center gap-1">
                                                        <UserCog className="w-3 h-3" /> Current: {batch.assigned_to.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-amber-500">Unassigned</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-20 bg-zinc-50 dark:bg-zinc-900/30 rounded-xl border border-dashed border-zinc-200 flex flex-col items-center">
                                <Truck className="w-12 h-12 text-zinc-300 mb-3" />
                                <p className="text-zinc-500 font-medium">No batches found matching your criteria.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={!!reassignmentData} onOpenChange={(open) => !open && setReassignmentData(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Reassignment</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to reassign Batch <span className="font-mono font-bold text-zinc-900 dark:text-zinc-50">{reassignmentData?.batchDisplayId}</span> to <span className="font-bold text-zinc-900 dark:text-zinc-50">{reassignmentData?.staffName}</span>?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReassignmentData(null)}>Cancel</Button>
                        <Button
                            onClick={() => {
                                if (reassignmentData) {
                                    reassignBatchMutation.mutate({
                                        batchId: reassignmentData.batchId,
                                        staffId: reassignmentData.staffId
                                    });
                                }
                            }}
                            disabled={reassignBatchMutation.isPending}
                        >
                            {reassignBatchMutation.isPending ? "Reassigning..." : "Confirm Reassignment"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
